import { useServerTime } from "$global-providers/server-time-provider/provider";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { useMemo, useState } from "react";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../_lib/epocus-palette";
import { Card, PageRoot } from "../_lib/stat-primitives";
import { selectActiveProgram, useMyPrograms } from "../_lib/use-active-program";
import { useEpocusProgramDashboardQuery } from "./_lib/__generated__/program-dashboard-operations.gql";
import {
  displayName,
  formatDelta,
  formatMinutes,
  formatPct,
  formatTurnaroundTarget,
} from "./_lib/admin-metrics";
import {
  CardHeading,
  CardNote,
  KpiCard,
  KpiGrid,
  PageTitle,
  ShareBar,
  TwoColumn,
} from "./_lib/admin-primitives";
import {
  DEFAULT_PROGRAM_PERIOD,
  getProgramPeriodRange,
  type ProgramPeriodKey,
} from "./_lib/program-period";
import { ProgramPeriodSelector } from "./_lib/program-period-selector";
import { WeeklyCoverageChart } from "./_lib/weekly-coverage-chart";

const greeting = (hour: number): string => {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const IntegrityRow: FC<{ label: string; value: number }> = ({
  label,
  value,
}) => (
  <Box
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "10px 0",
    }}
  >
    <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
      {label}
    </Typography>
    <Typography
      style={{ fontSize: rem(15), fontWeight: 700, color: EPOCUS_TEXT_PRIMARY }}
    >
      {value}
    </Typography>
  </Box>
);

export const EpocusProgramPage: FC = () => {
  const { user } = useAuth0();
  const { getCurrentTime } = useServerTime();
  const { programs, loading: programsLoading } = useMyPrograms();
  const program = selectActiveProgram(programs);
  const programId = program?.id ?? null;

  const [period, setPeriod] = useState<ProgramPeriodKey>(
    DEFAULT_PROGRAM_PERIOD,
  );
  const [now] = useState(getCurrentTime);
  const range = useMemo(
    () => getProgramPeriodRange(period, now.getTime()),
    [period, now],
  );

  const { data, loading, error } = useEpocusProgramDashboardQuery({
    variables: { programId: programId ?? "", range },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });

  const dashboard = data?.epocusProgramDashboard;

  const shortName = user?.name?.split(" ").slice(-1)[0] ?? "there";

  return (
    <PageRoot>
      <Box
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <PageTitle
          title={`${greeting(now.getHours())}, ${shortName}`}
          subtitle="Quality and throughput across the program"
        />
        <ProgramPeriodSelector period={period} onChange={setPeriod} />
      </Box>

      {!dashboard && (programsLoading || loading) ? (
        <Box style={{ display: "flex", justifyContent: "center", padding: 32 }}>
          <CircularProgress aria-label="Loading the program dashboard" />
        </Box>
      ) : !programId ? (
        <Card>
          <CardNote>Couldn't determine your program.</CardNote>
        </Card>
      ) : !dashboard && error ? (
        <Card>
          <CardNote>Couldn't load the program dashboard.</CardNote>
        </Card>
      ) : dashboard ? (
        <>
          <KpiGrid>
            <KpiCard
              label="Scans this period"
              value={String(dashboard.scans.current)}
              caption={formatDelta(dashboard.scans.deltaPct)}
            />
            <KpiCard
              label="QA reviewed"
              value={formatPct(dashboard.qaReviewedPct)}
              caption={`${dashboard.qaReviewedCount} of ${dashboard.scans.current}`}
            />
            <KpiCard
              label="Completed within target"
              value={formatPct(dashboard.completedWithinTargetPct)}
              caption={formatTurnaroundTarget(
                dashboard.turnaroundTargetMinutes,
              )}
            />
            <KpiCard
              label="Open past target"
              value={String(dashboard.openPastTargetCount)}
              caption="needs action"
            />
          </KpiGrid>

          <TwoColumn>
            <Card>
              <CardHeading
                title="Scans & QA coverage"
                hint={`weekly, ${dashboard.weekly.length} weeks`}
              />
              {dashboard.weekly.length === 0 ? (
                <CardNote>No scans recorded in this period.</CardNote>
              ) : (
                <WeeklyCoverageChart weekly={dashboard.weekly} />
              )}
            </Card>

            <Card>
              <CardHeading title="Data integrity" hint="this period" />
              <IntegrityRow
                label="Studies documented"
                value={dashboard.integrity.documented}
              />
              <IntegrityRow
                label="Phantom / test scans"
                value={dashboard.integrity.testScans}
              />
              <IntegrityRow
                label="Uploaded with no patient link"
                value={dashboard.integrity.noPatientLink}
              />
              <IntegrityRow
                label="Escalations open"
                value={dashboard.integrity.escalationsOpen}
              />
            </Card>
          </TwoColumn>

          <Card>
            <CardHeading title="QA turnaround by reviewer" />
            {dashboard.turnaroundByReviewer.length === 0 ? (
              <CardNote>
                No reviewer has signed a study in this period.
              </CardNote>
            ) : (
              <Box
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {dashboard.turnaroundByReviewer.map((reviewer) => (
                  <Box
                    key={reviewer.reviewerId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: rem(13),
                        fontWeight: 600,
                        color: EPOCUS_TEXT_PRIMARY,
                        minWidth: 150,
                      }}
                    >
                      {displayName(reviewer.name, "reviewer")}
                    </Typography>
                    <Box style={{ flex: 1, minWidth: 160 }}>
                      <ShareBar
                        value={reviewer.withinTargetPct ?? null}
                        label={formatPct(reviewer.withinTargetPct)}
                      />
                    </Box>
                    <Typography
                      style={{
                        fontSize: rem(13),
                        color: EPOCUS_TEXT_SECONDARY,
                        minWidth: 72,
                        textAlign: "right",
                      }}
                    >
                      {formatMinutes(reviewer.medianMinutes)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            <CardNote>
              Median turnaround per reviewer; the bar shows the share of studies
              they reviewed within the program turnaround target.
            </CardNote>
          </Card>
        </>
      ) : null}
    </PageRoot>
  );
};
